import { FunctionComponent, memo, useContext } from "react";
import * as Yup from "yup";
import { IProps, ITagGroupFormValues } from "./tag-group-form.types";
import { Field, Form, Formik } from "formik";
import { Button, Checkbox, FieldError, SpinnerUntil, Tooltip } from "@edgetier/components";
import { faInfoCircle, faSave } from "@fortawesome/pro-solid-svg-icons";
import useTagGroups from "hooks-for/tag-groups/use-tag-groups";
import TagsListContext from "pages/tag-phrase-triggers/tags-list/tags-list-context/tags-list-context";
import { useUser } from "hooks-for/users/use-user";
import { useClient } from "hooks-for/clients/use-client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./tag-group-form.scss";

/**
 * A form for making changes to a tag group.
 * @param props.initialValues A tag group to initiate the form with.
 * @param props.onSubmit      A function that runs when the form is submitted.
 */
const TagGroupForm: FunctionComponent<IProps> = ({ initialValues, onSubmit }) => {
    const { showTagGroupUnaryForAgents } = useContext(TagsListContext);

    const { data: user } = useUser();
    const { data: client } = useClient(user?.clientId!);
    const { data: groupNames, isLoading } = useTagGroups(
        {},
        {
            select: (groups) =>
                groups.map(({ tagGroup }) => tagGroup).filter((tagGroup) => tagGroup !== initialValues.tagGroup),
        }
    );

    /**
     * Trim the values before calling the onSubmit function.
     * @param values The tag group values.
     */
    const preSubmit = (values: ITagGroupFormValues) => {
        const trimmed = { ...values, tagGroup: values.tagGroup.trim() };
        onSubmit(trimmed);
    };

    return (
        <SpinnerUntil data={groupNames} isReady={!isLoading}>
            <Formik
                initialValues={initialValues}
                onSubmit={preSubmit}
                validationSchema={Yup.object({
                    tagGroup: Yup.string()
                        .trim()
                        .required("Required")
                        .notOneOf(groupNames ?? [], "Duplicate Group Name"),
                })}
            >
                {({ values, isSubmitting }) => (
                    <Form name="tagGroupForm" className="tag-group-form">
                        <div className="field">
                            <label htmlFor="tagGroup">Tag Group</label>
                            <Field
                                type="text"
                                name="tagGroup"
                                id="tagGroup"
                                placeholder="New group name&hellip;"
                                autoFocus={true}
                            />
                            <FieldError name="tagGroup" />
                        </div>
                        <div className="field">
                            {showTagGroupUnaryForAgents ? (
                                <Checkbox
                                    name="isUnaryForAgents"
                                    label="Allow assignment of only one value per agent"
                                    checked={values.isUnaryForAgents}
                                />
                            ) : (
                                <Checkbox
                                    name="isUnaryForInteractions"
                                    label="Allow assignment of only one value per interaction"
                                    checked={values.isUnaryForInteractions}
                                />
                            )}
                        </div>
                        {typeof client !== "undefined" && client.isExternalTaggingEnabled && (
                            <div className="field external-toggle">
                                <Checkbox
                                    name="isExternalEnabled"
                                    label="Automatically apply tags in your source system"
                                    checked={values.isExternalEnabled}
                                />
                                <Tooltip
                                    content={
                                        <div className="external-toggle__tooltip-content">
                                            Please note that due to the limitations in some systems, <br />
                                            the name of the tag applied may not exactly match the name in WatchTower.
                                        </div>
                                    }
                                >
                                    <div className="tag-group-form__tooltip-icon">
                                        <FontAwesomeIcon icon={faInfoCircle} />
                                    </div>
                                </Tooltip>
                            </div>
                        )}
                        <Button icon={faSave} isLoading={isSubmitting} styleName="positive" type="submit">
                            Save
                        </Button>
                    </Form>
                )}
            </Formik>
        </SpinnerUntil>
    );
};

export default memo(TagGroupForm);
